import { Box, Button, Stack, Typography, Theme } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useFormik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';

import CheckIcon from '../../../assets/icons/checkIcon.svg?react';

import { OtpCodeInput } from '../../common/OtpCodeInput/OtpCodeInput';
import { PhoneVerificationProps } from './types';
import { localStorageService } from '../../../services/localStorageService/localStorageService';
import { AuthDeliveryChannel } from '@shared/enums/auth-delivery-channel.enum';
import { usePublicSettingsQuery } from '../../../hooks/useSettings';
import { useResendCooldown } from '../../../hooks/useResendCooldown';
import { useFormErrorHandler } from '../../../hooks/useFormErrorHandler';
import {
  getChannelIcon,
  sentViaKey,
  viaLabelKey,
} from '../../../constants/authChannels';

const CODE_LENGTH = 6;
const ACCENT = '#22c55e';
const ACCENT_HOVER = '#16a34a';

// Shared rounded-pill style for the action chips ("Send another way" channels
// and Resend) so they read as one consistent row.
const chipSx = {
  paddingX: '16px',
  paddingY: '8px',
  borderRadius: '999px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 14,
  color: '#fff',
  background: 'rgba(255, 255, 255, 0.07)',
  border: '1px solid rgba(255, 255, 255, 0.16)',
  '&:hover': {
    background: 'rgba(34, 197, 94, 0.14)',
    borderColor: ACCENT,
    color: ACCENT,
  },
  '&.Mui-disabled': {
    color: 'rgba(255, 255, 255, 0.4)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
} as const;

export const PhoneVerificationForm: React.FC<PhoneVerificationProps> = ({
  onSubmit,
  onResendCode,
  goToCabinet,
  offBoxShadow,
  descriptionText,
  deliveredVia,
  availableChannels,
  onSelectChannel,
  isReturningUser,
}) => {
  const { t } = useTranslation();
  const { data: publicSettings } = usePublicSettingsQuery();
  const telegramBotUsername = publicSettings?.telegramBotUsername;

  const { countdown, isCoolingDown, start: startCooldown, reset: resetCooldown } =
    useResendCooldown();
  const { serverError, setServerError, handleApiError } = useFormErrorHandler({
    validationError: t('errors.invalidEmailConfirmationCode'),
  });

  // Telegram is the highest-priority channel, so when it shows up as an
  // *alternative* it means the user hasn't linked the bot yet. Hide it when the
  // bot isn't configured, since there'd be nothing to open.
  const canShowChannel = (c: AuthDeliveryChannel) =>
    c !== AuthDeliveryChannel.TELEGRAM || !!telegramBotUsername;
  const alternativeChannels = (availableChannels ?? []).filter(
    (c) => c !== deliveredVia && canShowChannel(c),
  );

  const openTelegramBot = () => {
    if (!telegramBotUsername) return;
    // Pre-auth flow: the bot links the account by the phone the user shares,
    // then "Resend" delivers the code via Telegram.
    window.open(
      `https://t.me/${telegramBotUsername}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const handlePickChannel = async (channel: AuthDeliveryChannel) => {
    if (!onSelectChannel || isCoolingDown) return;
    setServerError(null);
    startCooldown();
    try {
      // Resend through the chosen channel. For Telegram this delivers the code
      // when the account is already linked to the bot.
      await onSelectChannel(channel);
    } catch (error) {
      // Roll the cooldown back so a failed send doesn't lock the user out.
      resetCooldown();
      if (channel === AuthDeliveryChannel.TELEGRAM) {
        // Telegram is offered but the account isn't linked yet, so the backend
        // can't deliver there — open the bot so the user can link it, then a
        // resend will arrive via Telegram.
        openTelegramBot();
        return;
      }
      handleApiError(error);
    }
  };

  const { values, setFieldValue, handleSubmit, errors } = useFormik({
    initialValues: {
      code: '',
    },
    onSubmit: async ({ code }) => {
      setServerError(null);
      try {
        await onSubmit(code);
        localStorageService().removeUnverifiedUserId();
      } catch (error) {
        handleApiError(error);
      }
    },
  });

  const resendCode = async () => {
    setServerError(null);
    startCooldown();
    try {
      await onResendCode();
    } catch (error) {
      // Roll the cooldown back so a failed resend doesn't lock the user out.
      resetCooldown();
      handleApiError(error);
    }
  };

  const handleCodeChange = (code: string) => {
    setFieldValue('code', code);
    setServerError(null);
  };

  const isComplete = values.code.length === CODE_LENGTH;

  const resendChip = (
    <Button
      onClick={resendCode}
      disabled={isCoolingDown}
      startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
      sx={chipSx}
    >
      {countdown
        ? `${t('userAuth.phoneConfirmation.resentCodeText')} (${countdown} ${t('userAuth.phoneConfirmation.sec')})`
        : t('userAuth.phoneConfirmation.resentCodeText')}
    </Button>
  );

  return (
    <Stack
      alignItems="center"
      textAlign="center"
      sx={(theme: Theme) => ({
        padding: { xs: goToCabinet ? '28px 20px 24px' : '28px 16px 16px' },
        gap: { xs: '22px' },
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto',
        zIndex: 1,
        background: goToCabinet ? theme.palette.gray[300] : 'transparent',
        borderRadius: goToCabinet ? '20px' : 0,
        boxShadow: offBoxShadow
          ? 'none'
          : `0px -32px 30px 15px ${theme.palette.gray[300]}`,
      })}
    >
      {goToCabinet ? (
        <Stack alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 76,
              height: 76,
              borderRadius: '50%',
              display: 'grid',
              placeContent: 'center',
              background: 'rgba(34, 197, 94, 0.12)',
              border: '1px solid rgba(34, 197, 94, 0.35)',
              '& svg': { width: 38, height: 38 },
            }}
          >
            <CheckIcon />
          </Box>
          <Stack gap={0.5}>
            <Typography
              color="green.main"
              sx={{ fontSize: 14, fontWeight: 600, letterSpacing: '0.02em' }}
            >
              {t(
                isReturningUser
                  ? 'userAuth.phoneConfirmation.signInTitlePart1'
                  : 'userAuth.phoneConfirmation.titlePart1',
              )}
            </Typography>
            <Typography
              color="common.white"
              sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}
            >
              {t(
                isReturningUser
                  ? 'userAuth.phoneConfirmation.signInTitlePart2'
                  : 'userAuth.phoneConfirmation.titlePart2',
              )}
            </Typography>
          </Stack>
        </Stack>
      ) : null}

      {descriptionText ? (
        <Typography
          color="common.white"
          sx={{ fontSize: 14, lineHeight: 1.5, opacity: 0.7 }}
        >
          {descriptionText}
        </Typography>
      ) : null}

      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          width: '100%',
        }}
      >
        <OtpCodeInput
          value={values.code}
          onChange={handleCodeChange}
          length={CODE_LENGTH}
          error={serverError || errors.code}
          autoFocus
        />

        {deliveredVia ? (
          <Stack alignItems="center" gap={1.25}>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              sx={{ color: 'rgba(255, 255, 255, 0.85)' }}
            >
              {getChannelIcon(deliveredVia, 28)}
              <Typography sx={{ fontSize: 14, color: 'inherit' }}>
                {t(sentViaKey[deliveredVia])}
              </Typography>
            </Stack>

            <Stack alignItems="center" gap={1}>
              {alternativeChannels.length > 0 && (
                <Typography
                  color="common.white"
                  sx={{ fontSize: 12, opacity: 0.55 }}
                >
                  {t('userAuth.phoneConfirmation.sendAnotherWay')}
                </Typography>
              )}
              <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="center">
                {alternativeChannels.map((channel) => (
                  <Button
                    key={channel}
                    onClick={() => handlePickChannel(channel)}
                    disabled={isCoolingDown}
                    startIcon={getChannelIcon(channel)}
                    sx={chipSx}
                  >
                    {t(viaLabelKey[channel])}
                  </Button>
                ))}
                {resendChip}
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <Stack alignItems="center" gap={1.5} sx={{ width: '100%' }}>
            <Stack
              gap={1.5}
              sx={(theme: Theme) => ({
                width: '100%',
                padding: '14px',
                borderRadius: '12px',
                backgroundColor: theme.palette.gray[200],
                border: '1px solid rgba(255, 255, 255, 0.12)',
                textAlign: 'left',
              })}
            >
            <Typography
              color="common.white"
              sx={{ fontSize: 13, lineHeight: 1.45 }}
            >
              {t('userAuth.phoneConfirmation.couldNotSend')}
            </Typography>
            {telegramBotUsername && (
              <>
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  onClick={openTelegramBot}
                  startIcon={
                    <img
                      src="/telegram-logo.png"
                      alt=""
                      width={32}
                      height={32}
                      style={{ display: 'block' }}
                    />
                  }
                  sx={{
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: 16,
                    paddingY: '12px',
                    textTransform: 'none',
                    backgroundColor: '#229ED9',
                    color: '#fff',
                    boxShadow: '0 6px 18px rgba(34, 158, 217, 0.45)',
                    '&:hover': { backgroundColor: '#1c87ba' },
                  }}
                >
                  {t('userAuth.phoneConfirmation.connectTelegram')}
                </Button>
                <Typography
                  color="common.white"
                  sx={{ fontSize: 12, lineHeight: 1.45, opacity: 0.7 }}
                >
                  {t('userAuth.phoneConfirmation.connectTelegramHint')}
                </Typography>
                </>
              )}
            </Stack>
            {resendChip}
          </Stack>
        )}

        <Button
          variant="contained"
          type="submit"
          fullWidth
          disabled={!isComplete}
          sx={{
            borderRadius: '14px',
            paddingY: '13px',
            fontSize: 16,
            fontWeight: 700,
            textTransform: 'none',
            backgroundColor: ACCENT,
            color: '#06281a',
            boxShadow: '0 8px 20px rgba(34, 197, 94, 0.28)',
            '&:hover': { backgroundColor: ACCENT_HOVER },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              color: 'rgba(255, 255, 255, 0.35)',
              boxShadow: 'none',
            },
          }}
        >
          {t('userAuth.phoneConfirmation.btnText')}
        </Button>
      </form>
    </Stack>
  );
};
